import * as React from 'react';
import { View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { rem } from 'design-system/values';
import { IconSettings } from 'design-system/icons/IconSettings';
import ChartLoadingOverlay from 'design-system/LineChart/ChartLoadingOverlay';
import ScrollableTimeFrameController from 'design-system/LineChart/ScrollableTimeFrameController';
import { STOCK_CHART_DEFAULT_PERIODS, STOCK_CHART_DEFAULT_PERIODS_WITHOUT_1D, } from 'design-system/LineChart/useLineGraphData';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import StockLineChart from './StockLineChart';
import StockCandleChart from './StockCandleChart';
import { CHART_HEIGHT, STOCK_DEFAULT_CHART } from '../constants';
import { StockChartSettingsModal } from './StockChartSettingsModal';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
const SETTINGS_BTN_WIDTH = rem(56);
const PADDING_HORIZONTAL = rem(16);
const SETTINGS_BTN_SIZE = rem(40);
const StockChartView = ({ dataObj, has1DChart, requesting, selectedPeriod, selectedInfoObj, setSelectedPeriod, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const investStockChartSettings = useAppSelector((state) => state.deviceSettings.investStockChartSettings);
    const chart = investStockChartSettings?.chart || STOCK_DEFAULT_CHART;
    const showBuyAndSell = investStockChartSettings?.shouldShowBuyAndSell || false;
    const strokeColor = React.useMemo(() => {
        if (dataObj?.strokeColorKey) {
            // @ts-ignore
            const computedColor = colors.elements[dataObj.strokeColorKey];
            return computedColor ?? colors.elements.secondary;
        }
        return colors.elements.secondary;
    }, [colors.elements, dataObj?.strokeColorKey]);
    const onSave = React.useCallback((chartType, shouldShowBuyAndSell) => {
        dispatch(setDeviceSettingsKey('investStockChartSettings', { chart: chartType, shouldShowBuyAndSell }));
    }, []);
    const handleSettingsPress = React.useCallback(() => {
        StockChartSettingsModal.show(chart, showBuyAndSell, onSave);
    }, [chart, onSave, showBuyAndSell]);
    const chartRender = React.useMemo(() => {
        if (!isAlpacaAccount || chart === 'line') {
            return (<StockLineChart dataObj={dataObj} strokeColor={strokeColor} showBuyAndSell={showBuyAndSell} selectedPeriod={selectedPeriod} selectedInfoObj={selectedInfoObj}/>);
        }
        if (!dataObj?.candleData?.length) {
            return null;
        }
        return <StockCandleChart dataObj={dataObj} selectedInfoObj={selectedInfoObj}/>;
    }, [chart, dataObj, isAlpacaAccount, selectedInfoObj, selectedPeriod, showBuyAndSell, strokeColor]);
    const periods = React.useMemo(() => {
        if (has1DChart) {
            return STOCK_CHART_DEFAULT_PERIODS;
        }
        return STOCK_CHART_DEFAULT_PERIODS_WITHOUT_1D;
    }, [has1DChart]);
    return (<View style={styles.container}>
      <View style={styles.row}>
        <View style={styles.flex}>
          <ScrollableTimeFrameController periods={periods} darkActiveIndicator strokeColor={strokeColor} scrollToEnd={!has1DChart} selectedPeriod={selectedPeriod} paddingHorizontal={PADDING_HORIZONTAL} minusWidth={has1DChart ? SETTINGS_BTN_WIDTH : 0} setSelectedPeriod={setSelectedPeriod}/>
        </View>
        {has1DChart ? (<TouchableBounce style={styles.settingsBtn} onPress={handleSettingsPress}>
            <IconSettings color={colors.elements.secondary}/>
          </TouchableBounce>) : null}
      </View>
      <View style={styles.chartContainer}>
        {chartRender}
        {requesting ? <ChartLoadingOverlay requesting={requesting}/> : null}
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flexDirection: 'column-reverse',
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    chartContainer: {
        height: CHART_HEIGHT,
    },
    flex: {
        flex: 1,
    },
    settingsBtn: {
        marginHorizontal: rem(8),
        borderRadius: rem(8),
        alignItems: 'center',
        justifyContent: 'center',
        width: SETTINGS_BTN_SIZE,
        height: SETTINGS_BTN_SIZE,
        backgroundColor: colors.background.dark,
    },
}));
export default React.memo(StockChartView);
